import { CheckOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { ConfigProvider } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'sutra/components/Button'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import {
  PriorPeriodEntryColumn,
  PriorPeriodGroupColumn,
} from 'components/date-range-picker/CascadingDateMenu/PriorPeriodColumns'
import {
  ANTD_POPUP_THEME,
  CalendarColumn,
  CalendarGrid,
  CalendarLabel,
  CalendarsRow,
  Column,
  CustomColumn,
  DateTextInput,
  DayCell,
  DayCellSpacer,
  DaysGrid,
  FixedPanel,
  LastControlsRow,
  LastPanel,
  LastUnitSelect,
  LastValueInput,
  MenuDivider,
  MenuRoot,
  MenuRow,
  MenuRowWithArrow,
  MonthNavRow,
  MonthSelect,
  RangeArrow,
  SinceDatePicker,
  SincePanel,
  ToggleRow,
  WeekdayCell,
  WeekdayRow,
  YearSelect,
} from 'components/date-range-picker/CascadingDateMenu/styles'
import {
  SHORTHAND_DATE_OPTIONS,
  XTD_DATE_OPTIONS,
} from 'components/date-range-picker/date-defaults'
import type {
  DateRangePresetSource,
  DateSelectionMeta,
  PriorPeriodEntry,
  PriorPeriodGroup,
} from 'components/date-range-picker/types'
import {
  toUtcDayjs,
  utcEndOfDay,
  utcStartOfDay,
} from 'components/date-range-picker/utc-date-utils'

// The calendar is internally a UTC-mode dayjs calendar: every dayjs below is
// UTC so cells, endpoints, and "today" compare consistently and `.toDate()`
// stores UTC midnight. Extend the utc plugin here rather than rely on another
// module having been imported first.
dayjs.extend(utc)

interface CascadingDateMenuProps {
  dateRange: {
    startDate: Date | null
    endDate: Date | null
  }
  onDateRangeChange: (
    startDate: Date,
    endDate: Date,
    source: DateRangePresetSource,
    value?: string,
    meta?: DateSelectionMeta,
  ) => void
  parseFlexibleDate: (input: string) => { date: Dayjs | null; error: string }
  calculateDateRange: (
    mode: string,
    value?: number,
    unit?: string,
    since?: Dayjs,
  ) => { startDate: Date; endDate: Date }
  selectionSource: DateRangePresetSource | 'priorPeriod' | null
  selectedValue: string | null
  isOpen: boolean

  // ── Sync to dashboard (optional; row hidden when onSync is unset) ──────
  onSync?: () => void
  isSynced?: boolean

  // ── Most recent toggle (optional; row hidden when mostRecent is undefined) ──
  mostRecent?: boolean
  mostRecentDisabled?: boolean
  onMostRecentChange?: (next: boolean) => void

  // ── Prior Period cascade (optional; row hidden when empty) ─────────────
  priorPeriods?: readonly PriorPeriodGroup[]
  onPriorPeriodSelect?: (groupKey: string, entry: PriorPeriodEntry) => void
}

export const CascadingDateMenu: React.FC<CascadingDateMenuProps> = ({
  dateRange,
  onDateRangeChange,
  parseFlexibleDate,
  calculateDateRange,
  selectionSource,
  selectedValue,
  isOpen,
  onSync,
  isSynced,
  mostRecent,
  mostRecentDisabled,
  onMostRecentChange,
  priorPeriods,
  onPriorPeriodSelect,
}) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [hoveredPriorGroupKey, setHoveredPriorGroupKey] = useState<
    string | null
  >(null)
  const [activeCustomTab, setActiveCustomTab] = useState<string>('fixed')

  // Reset the sub-column state when the dropdown closes so a fresh open
  // always shows just the preset list (not the sticky custom panel).
  useEffect(() => {
    if (!isOpen) {
      setHoveredItem(null)
      setHoveredPriorGroupKey(null)
    }
  }, [isOpen])

  // State for custom date selection
  const [lastValue, setLastValue] = useState(70)
  const [lastUnit, setLastUnit] = useState<'days' | 'weeks' | 'months'>('days')
  const [sinceDate, setSinceDate] = useState<Dayjs | null>(
    dateRange.startDate
      ? toUtcDayjs(dateRange.startDate)
      : dayjs.utc().subtract(6, 'month'),
  )
  const [startDateInput, setStartDateInput] = useState('')
  const [endDateInput, setEndDateInput] = useState('')
  const [startDateError, setStartDateError] = useState('')
  const [endDateError, setEndDateError] = useState('')
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(null)
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(null)

  // Track whether Fixed-panel text inputs have been edited since last focus.
  // Refs (not state) so flipping them never triggers a re-render.
  const startInputDirtyRef = useRef(false)
  const endInputDirtyRef = useRef(false)

  // State for dual calendar month navigation
  const [leftDisplayMonth, setLeftDisplayMonth] = useState<Dayjs>(
    dayjs.utc().subtract(1, 'month'),
  )
  const [rightDisplayMonth, setRightDisplayMonth] = useState<Dayjs>(dayjs.utc())

  // Debounced handler for date range changes
  const debouncedOnDateRangeChange = useMemo(
    () =>
      debounce(
        (
          startDate: Date,
          endDate: Date,
          source: 'shorthand' | 'xtd' | 'custom',
          value?: string,
        ) => {
          onDateRangeChange(startDate, endDate, source, value)
        },
        500, // Wait 500ms after user stops interacting
      ),
    [onDateRangeChange],
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedOnDateRangeChange.cancel()
    }
  }, [debouncedOnDateRangeChange])

  // Fixed-panel text inputs auto-submit on a 2s pause. Separate from the 500ms
  // debouncedOnDateRangeChange (Since/Last) so the two cadences stay independent.
  // Resets the dirty refs so a debounce-fired submit isn't re-fired by a later blur.
  const debouncedFixedSubmit = useMemo(
    () =>
      debounce((startDate: Date, endDate: Date) => {
        startInputDirtyRef.current = false
        endInputDirtyRef.current = false
        onDateRangeChange(startDate, endDate, 'custom')
      }, 1500),
    [onDateRangeChange],
  )
  useEffect(() => () => debouncedFixedSubmit.cancel(), [debouncedFixedSubmit])

  // Initialize selected dates from dateRange
  React.useEffect(() => {
    if (dateRange.startDate) {
      const startDate = toUtcDayjs(dateRange.startDate)
      setSelectedStartDate(startDate)
      setStartDateInput(startDate.format('MMM D, YYYY'))
      setLeftDisplayMonth(startDate)
    }
    if (dateRange.endDate) {
      const endDate = toUtcDayjs(dateRange.endDate)
      setSelectedEndDate(endDate)
      setEndDateInput(endDate.format('MMM D, YYYY'))
      setRightDisplayMonth(endDate)
    }
  }, [dateRange.startDate, dateRange.endDate])

  // Immediate submit for Enter / blur. Cancels any pending 2s debounce so the
  // explicit action and the timer can't double-fire.
  const submitFixedRangeNow = useCallback(() => {
    debouncedFixedSubmit.cancel()
    if (
      startDateError ||
      endDateError ||
      !selectedStartDate ||
      !selectedEndDate ||
      selectedStartDate.isAfter(selectedEndDate)
    ) {
      return
    }
    startInputDirtyRef.current = false
    endInputDirtyRef.current = false
    onDateRangeChange(
      utcStartOfDay(selectedStartDate).toDate(),
      utcEndOfDay(selectedEndDate).toDate(),
      'custom',
    )
  }, [
    debouncedFixedSubmit,
    startDateError,
    endDateError,
    selectedStartDate,
    selectedEndDate,
    onDateRangeChange,
  ])

  const shorthandOptions = SHORTHAND_DATE_OPTIONS

  const xtdOptions = XTD_DATE_OPTIONS

  const handleShorthandClick = (key: string): void => {
    const { startDate, endDate } = calculateDateRange(key)
    onDateRangeChange(startDate, endDate, 'shorthand', key)
  }

  const handleXtdClick = (key: string): void => {
    const { startDate, endDate } = calculateDateRange(key)
    onDateRangeChange(startDate, endDate, 'xtd', key)
  }

  const isDateInRange = useCallback(
    (date: Dayjs) => {
      if (!selectedStartDate) return false
      if (!selectedEndDate) return date.isSame(selectedStartDate, 'day')
      return date.isBetween(selectedStartDate, selectedEndDate, 'day', '[]')
    },
    [selectedStartDate, selectedEndDate],
  )

  const isRangeEndpoint = useCallback(
    (date: Dayjs) => {
      return (
        (selectedStartDate && date.isSame(selectedStartDate, 'day')) ||
        (selectedEndDate && date.isSame(selectedEndDate, 'day'))
      )
    },
    [selectedStartDate, selectedEndDate],
  )

  // New handlers for dual calendar
  const handleStartCalendarClick = useCallback(
    (date: Dayjs) => {
      setSelectedStartDate(date)
      setStartDateInput(date.format('MMM D, YYYY'))
      setStartDateError('')
      // Auto-advance end date if it's before the new start date
      if (selectedEndDate && date.isAfter(selectedEndDate)) {
        setSelectedEndDate(null)
        setEndDateInput('')
      } else if (selectedEndDate) {
        // Both dates are now valid, apply the range immediately (no debounce)
        onDateRangeChange(
          utcStartOfDay(date).toDate(),
          utcEndOfDay(selectedEndDate).toDate(),
          'custom',
        )
      }
    },
    [selectedEndDate, onDateRangeChange],
  )

  const handleEndCalendarClick = useCallback(
    (date: Dayjs) => {
      setSelectedEndDate(date)
      setEndDateInput(date.format('MMM D, YYYY'))
      setEndDateError('')
      // Auto-advance start date if it's after the new end date
      if (selectedStartDate && date.isBefore(selectedStartDate)) {
        setSelectedStartDate(null)
        setStartDateInput('')
      } else if (selectedStartDate) {
        // Both dates are now valid, apply the range immediately (no debounce)
        onDateRangeChange(
          utcStartOfDay(selectedStartDate).toDate(),
          utcEndOfDay(date).toDate(),
          'custom',
        )
      }
    },
    [selectedStartDate, onDateRangeChange],
  )

  // Generate a single month calendar
  const generateSingleMonth = useCallback((month: Dayjs) => {
    const monthStart = month.startOf('month')
    const monthEnd = month.endOf('month')
    const firstDayOfWeek = monthStart.day() // 0 = Sunday

    const days = []

    // Add empty cells for days before the first day of the month
    for (let j = 0; j < firstDayOfWeek; j++) {
      days.push(null)
    }

    // Add all days of the month
    for (let day = 1; day <= monthEnd.date(); day++) {
      days.push(month.date(day))
    }

    return days
  }, [])

  const showSecondColumn =
    hoveredItem === 'xtd' ||
    hoveredItem === 'custom' ||
    hoveredItem === 'priorPeriod'
  const showThirdColumn =
    hoveredItem === 'custom' ||
    (hoveredItem === 'priorPeriod' && hoveredPriorGroupKey !== null)
  const hasFooter = Boolean(onSync) || mostRecent !== undefined
  const hoveredPriorGroup = priorPeriods?.find(
    (group) => group.key === hoveredPriorGroupKey,
  )

  return (
    <ConfigProvider theme={ANTD_POPUP_THEME}>
      <MenuRoot>
        {/* Column 1: Main options */}
        <Column
          $width={168}
          $divided={showSecondColumn}
          onMouseLeave={() => {
            // Only reset hover if we're not hovering over columns 2 or 3
            if (!showSecondColumn) {
              setHoveredItem(null)
              setHoveredPriorGroupKey(null)
            }
          }}
        >
          {shorthandOptions.map((option) => {
            const isSelected =
              selectionSource === 'shorthand' && selectedValue === option.key
            return (
              <MenuRow
                key={option.key}
                $selected={isSelected}
                onClick={() => handleShorthandClick(option.key)}
                onMouseEnter={() => setHoveredItem(null)}
              >
                {option.label}
              </MenuRow>
            )
          })}
          {priorPeriods && priorPeriods.length > 0 && (
            <MenuRowWithArrow
              $selected={selectionSource === 'priorPeriod'}
              $active={
                hoveredItem === 'priorPeriod' ||
                selectionSource === 'priorPeriod'
              }
              onMouseEnter={() => setHoveredItem('priorPeriod')}
            >
              Prior Period
              <RightOutlined />
            </MenuRowWithArrow>
          )}
          <MenuRowWithArrow
            $selected={selectionSource === 'xtd'}
            $active={hoveredItem === 'xtd' || selectionSource === 'xtd'}
            onMouseEnter={() => setHoveredItem('xtd')}
          >
            X to Date
            <RightOutlined />
          </MenuRowWithArrow>
          <MenuRowWithArrow
            $selected={selectionSource === 'custom'}
            $active={hoveredItem === 'custom' || selectionSource === 'custom'}
            onMouseEnter={() => setHoveredItem('custom')}
          >
            Custom
            <RightOutlined />
          </MenuRowWithArrow>
          {hasFooter && (
            <>
              <MenuDivider />
              {mostRecent !== undefined && (
                <Tooltip
                  content={
                    mostRecentDisabled
                      ? 'Pick a recomputable preset (e.g. Last 6 months) to enable Most recent.'
                      : undefined
                  }
                >
                  <ToggleRow $disabled={mostRecentDisabled}>
                    <Checkbox
                      checked={mostRecent}
                      disabled={mostRecentDisabled}
                      onChange={(checked) => onMostRecentChange?.(checked)}
                    />
                    Most recent
                  </ToggleRow>
                </Tooltip>
              )}
              {onSync && (
                <MenuRowWithArrow
                  $selected={Boolean(isSynced)}
                  onClick={isSynced ? undefined : onSync}
                >
                  Sync to dashboard
                  {isSynced && <CheckOutlined />}
                </MenuRowWithArrow>
              )}
            </>
          )}
        </Column>

        {/* Column 2: Sub-options */}
        {showSecondColumn && (
          <Column
            $width={
              hoveredItem === 'xtd' || hoveredItem === 'priorPeriod' ? 150 : 120
            }
            $divided={showThirdColumn}
            onMouseLeave={() => {
              // Only reset if we're not moving to column 3
              if (!showThirdColumn) {
                setHoveredItem(null)
                setHoveredPriorGroupKey(null)
              }
            }}
          >
            {hoveredItem === 'xtd' &&
              xtdOptions.map((option) => {
                const isSelected =
                  selectionSource === 'xtd' && selectedValue === option.key
                return (
                  <MenuRow
                    key={option.key}
                    $selected={isSelected}
                    onClick={() => handleXtdClick(option.key)}
                  >
                    {option.label}
                  </MenuRow>
                )
              })}
            {hoveredItem === 'priorPeriod' && priorPeriods && (
              <PriorPeriodGroupColumn
                groups={priorPeriods}
                hoveredGroupKey={hoveredPriorGroupKey}
                selectedGroupKey={
                  selectionSource === 'priorPeriod'
                    ? (selectedValue?.split(':')[0] ?? null)
                    : null
                }
                onHoverGroup={setHoveredPriorGroupKey}
              />
            )}
            {hoveredItem === 'custom' && (
              <>
                <MenuRow
                  $selected={activeCustomTab === 'fixed'}
                  onMouseEnter={() => setActiveCustomTab('fixed')}
                >
                  Fixed
                </MenuRow>
                <MenuRow
                  $selected={activeCustomTab === 'since'}
                  onMouseEnter={() => setActiveCustomTab('since')}
                >
                  Since
                </MenuRow>
                <MenuRow
                  $selected={activeCustomTab === 'last'}
                  onMouseEnter={() => setActiveCustomTab('last')}
                >
                  Last
                </MenuRow>
              </>
            )}
          </Column>
        )}

        {/* Column 3: Prior Period entries */}
        {hoveredItem === 'priorPeriod' && hoveredPriorGroup && (
          <PriorPeriodEntryColumn
            entries={hoveredPriorGroup.entries}
            selectedEntryKey={
              selectionSource === 'priorPeriod' ? selectedValue : null
            }
            onSelect={(entry) =>
              onPriorPeriodSelect?.(hoveredPriorGroup.key, entry)
            }
          />
        )}

        {/* Column 3: Custom panels */}
        {hoveredItem === 'custom' && (
          <CustomColumn
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {activeCustomTab === 'fixed' && (
              <FixedPanel>
                {/* Dual Calendar Layout */}
                <CalendarsRow>
                  {/* Left Calendar - Start Date */}
                  <CalendarColumn>
                    <CalendarLabel>Starts</CalendarLabel>
                    <DateTextInput
                      value={startDateInput}
                      onChange={(e) => {
                        const value = e.target.value
                        startInputDirtyRef.current = true
                        setStartDateInput(value)
                        const { date, error } = parseFlexibleDate(value)
                        setStartDateError(error)
                        if (date && !error) {
                          setSelectedStartDate(date)
                          setLeftDisplayMonth(date)
                          if (
                            selectedEndDate &&
                            !date.isAfter(selectedEndDate)
                          ) {
                            debouncedFixedSubmit(
                              utcStartOfDay(date).toDate(),
                              utcEndOfDay(selectedEndDate).toDate(),
                            )
                          } else {
                            debouncedFixedSubmit.cancel()
                          }
                        } else {
                          debouncedFixedSubmit.cancel()
                        }
                      }}
                      onPressEnter={() => submitFixedRangeNow()}
                      onBlur={() => {
                        if (!startInputDirtyRef.current) return
                        submitFixedRangeNow()
                      }}
                      placeholder="Jan 1, 2026"
                      $size="xs"
                      $error={!!startDateError}
                    />

                    {/* Month/Year Navigation */}
                    <MonthNavRow>
                      <Button
                        variant="secondary"
                        size="xs"
                        aria-label="Previous month"
                        icon={<LeftOutlined />}
                        onClick={() =>
                          setLeftDisplayMonth(
                            leftDisplayMonth.subtract(1, 'month'),
                          )
                        }
                      />
                      <MonthSelect
                        value={leftDisplayMonth.month()}
                        onChange={(month) =>
                          setLeftDisplayMonth(
                            leftDisplayMonth.month(month as number),
                          )
                        }
                        options={[
                          { label: 'January', value: 0 },
                          { label: 'February', value: 1 },
                          { label: 'March', value: 2 },
                          { label: 'April', value: 3 },
                          { label: 'May', value: 4 },
                          { label: 'June', value: 5 },
                          { label: 'July', value: 6 },
                          { label: 'August', value: 7 },
                          { label: 'September', value: 8 },
                          { label: 'October', value: 9 },
                          { label: 'November', value: 10 },
                          { label: 'December', value: 11 },
                        ]}
                      />
                      <YearSelect
                        value={leftDisplayMonth.year()}
                        onChange={(year) =>
                          setLeftDisplayMonth(
                            leftDisplayMonth.year(year as number),
                          )
                        }
                        options={Array.from({ length: 11 }, (_, i) => ({
                          label: String(dayjs.utc().year() - 5 + i),
                          value: dayjs.utc().year() - 5 + i,
                        }))}
                      />
                      <Button
                        variant="secondary"
                        size="xs"
                        aria-label="Next month"
                        icon={<RightOutlined />}
                        onClick={() =>
                          setLeftDisplayMonth(leftDisplayMonth.add(1, 'month'))
                        }
                      />
                    </MonthNavRow>

                    {/* Calendar Grid */}
                    <CalendarGrid>
                      {/* Days of Week Header */}
                      <WeekdayRow>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                          (day) => (
                            <WeekdayCell key={day}>{day}</WeekdayCell>
                          ),
                        )}
                      </WeekdayRow>

                      {/* Days Grid */}
                      <DaysGrid>
                        {generateSingleMonth(leftDisplayMonth).map(
                          (date, dayIndex) => {
                            if (!date) {
                              return <DayCellSpacer key={`empty-${dayIndex}`} />
                            }

                            const isInRange = isDateInRange(date)
                            const isEndpoint = isRangeEndpoint(date)
                            const isToday = date.isSame(dayjs.utc(), 'day')
                            const isFuture = date.isAfter(dayjs.utc(), 'day')

                            return (
                              <DayCell
                                key={date.format('YYYY-MM-DD')}
                                onClick={() =>
                                  !isFuture && handleStartCalendarClick(date)
                                }
                                disabled={isFuture}
                                $endpoint={Boolean(isEndpoint)}
                                $inRange={isInRange}
                                $today={isToday}
                                $future={isFuture}
                              >
                                {date.date()}
                              </DayCell>
                            )
                          },
                        )}
                      </DaysGrid>
                    </CalendarGrid>
                  </CalendarColumn>

                  {/* Arrow Separator */}
                  <RangeArrow>→</RangeArrow>

                  {/* Right Calendar - End Date */}
                  <CalendarColumn>
                    <CalendarLabel>Ends</CalendarLabel>
                    <DateTextInput
                      value={endDateInput}
                      onChange={(e) => {
                        const value = e.target.value
                        endInputDirtyRef.current = true
                        setEndDateInput(value)
                        const { date, error } = parseFlexibleDate(value)
                        setEndDateError(error)
                        if (date && !error) {
                          setSelectedEndDate(date)
                          setRightDisplayMonth(date)
                          if (
                            selectedStartDate &&
                            !date.isBefore(selectedStartDate)
                          ) {
                            debouncedFixedSubmit(
                              utcStartOfDay(selectedStartDate).toDate(),
                              utcEndOfDay(date).toDate(),
                            )
                          } else {
                            debouncedFixedSubmit.cancel()
                          }
                        } else {
                          debouncedFixedSubmit.cancel()
                        }
                      }}
                      onPressEnter={() => submitFixedRangeNow()}
                      onBlur={() => {
                        if (!endInputDirtyRef.current) return
                        submitFixedRangeNow()
                      }}
                      placeholder="Feb 10, 2026"
                      $size="xs"
                      $error={!!endDateError}
                    />

                    {/* Month/Year Navigation */}
                    <MonthNavRow>
                      <Button
                        variant="secondary"
                        size="xs"
                        aria-label="Previous month"
                        icon={<LeftOutlined />}
                        onClick={() =>
                          setRightDisplayMonth(
                            rightDisplayMonth.subtract(1, 'month'),
                          )
                        }
                      />
                      <MonthSelect
                        value={rightDisplayMonth.month()}
                        onChange={(month) =>
                          setRightDisplayMonth(
                            rightDisplayMonth.month(month as number),
                          )
                        }
                        options={[
                          { label: 'January', value: 0 },
                          { label: 'February', value: 1 },
                          { label: 'March', value: 2 },
                          { label: 'April', value: 3 },
                          { label: 'May', value: 4 },
                          { label: 'June', value: 5 },
                          { label: 'July', value: 6 },
                          { label: 'August', value: 7 },
                          { label: 'September', value: 8 },
                          { label: 'October', value: 9 },
                          { label: 'November', value: 10 },
                          { label: 'December', value: 11 },
                        ]}
                      />
                      <YearSelect
                        value={rightDisplayMonth.year()}
                        onChange={(year) =>
                          setRightDisplayMonth(
                            rightDisplayMonth.year(year as number),
                          )
                        }
                        options={Array.from({ length: 11 }, (_, i) => ({
                          label: String(dayjs.utc().year() - 5 + i),
                          value: dayjs.utc().year() - 5 + i,
                        }))}
                      />
                      <Button
                        variant="secondary"
                        size="xs"
                        aria-label="Next month"
                        icon={<RightOutlined />}
                        onClick={() =>
                          setRightDisplayMonth(
                            rightDisplayMonth.add(1, 'month'),
                          )
                        }
                      />
                    </MonthNavRow>

                    {/* Calendar Grid */}
                    <CalendarGrid>
                      {/* Days of Week Header */}
                      <WeekdayRow>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                          (day) => (
                            <WeekdayCell key={day}>{day}</WeekdayCell>
                          ),
                        )}
                      </WeekdayRow>

                      {/* Days Grid */}
                      <DaysGrid>
                        {generateSingleMonth(rightDisplayMonth).map(
                          (date, dayIndex) => {
                            if (!date) {
                              return <DayCellSpacer key={`empty-${dayIndex}`} />
                            }

                            const isInRange = isDateInRange(date)
                            const isEndpoint = isRangeEndpoint(date)
                            const isToday = date.isSame(dayjs.utc(), 'day')
                            const isFuture = date.isAfter(dayjs.utc(), 'day')

                            return (
                              <DayCell
                                key={date.format('YYYY-MM-DD')}
                                onClick={() =>
                                  !isFuture && handleEndCalendarClick(date)
                                }
                                disabled={isFuture}
                                $endpoint={Boolean(isEndpoint)}
                                $inRange={isInRange}
                                $today={isToday}
                                $future={isFuture}
                              >
                                {date.date()}
                              </DayCell>
                            )
                          },
                        )}
                      </DaysGrid>
                    </CalendarGrid>
                  </CalendarColumn>
                </CalendarsRow>
              </FixedPanel>
            )}

            {activeCustomTab === 'since' && (
              <SincePanel>
                <SinceDatePicker
                  value={sinceDate}
                  onChange={(date) => {
                    const nextDate = date as Dayjs | null
                    setSinceDate(nextDate)
                    if (nextDate) {
                      const { startDate, endDate } = calculateDateRange(
                        'since',
                        undefined,
                        undefined,
                        nextDate,
                      )
                      debouncedOnDateRangeChange(startDate, endDate, 'custom')
                    }
                  }}
                  format="MMM D, YYYY"
                  size="small"
                />
              </SincePanel>
            )}

            {activeCustomTab === 'last' && (
              <LastPanel>
                <LastControlsRow>
                  <LastValueInput
                    value={lastValue}
                    onValueChange={(value) => {
                      const newValue = value || 70
                      setLastValue(newValue)
                      const { startDate, endDate } = calculateDateRange(
                        'last',
                        newValue,
                        lastUnit,
                      )
                      debouncedOnDateRangeChange(startDate, endDate, 'custom')
                    }}
                    min={1}
                    max={999}
                  />
                  <LastUnitSelect
                    value={lastUnit}
                    onChange={(unit) => {
                      const nextUnit = unit as 'days' | 'weeks' | 'months'
                      setLastUnit(nextUnit)
                      const { startDate, endDate } = calculateDateRange(
                        'last',
                        lastValue,
                        nextUnit,
                      )
                      debouncedOnDateRangeChange(startDate, endDate, 'custom')
                    }}
                    options={[
                      { label: 'days', value: 'days' },
                      { label: 'weeks', value: 'weeks' },
                      { label: 'months', value: 'months' },
                    ]}
                  />
                </LastControlsRow>
              </LastPanel>
            )}
          </CustomColumn>
        )}
      </MenuRoot>
    </ConfigProvider>
  )
}
